import React, {createContext, useCallback, useContext, useEffect, useState} from 'react'
import {UIGameCardAnimatorItemProps} from './UIGameCardAnimatorItem'
import {EventEmitter} from 'eventemitter3'
import {CardEnum} from '@colonist/game-states'

interface UIGameCardAnimatorContextProps {
    cards: UIGameCardAnimatorItemProps[]
    /**
     * @deprecated Define sizes using responsive CSS instead. Remove once UIGameCardInventory is converted to ReactJS
     */
    itemSize: number
}

const UIGameCardAnimatorContext = createContext<UIGameCardAnimatorContextProps | undefined>(undefined)

export const useUIGameCardAnimatorContext = () => {
    const context = useContext(UIGameCardAnimatorContext)
    if(!context) {
        throw new Error('useUIGameCardAnimatorContext must be used within a UIGameCardAnimatorProvider')
    }
    return context
}

interface UIGameCardAnimatorEvents {
    addCard: (item: UIGameCardAnimatorItemProps) => void
    removeCard: (id: number) => void
    forceAnimationComplete: (cardEnum: CardEnum) => void
}

export const UIGameCardAnimatorEventEmitter = new EventEmitter<UIGameCardAnimatorEvents>()

interface UIGameCardAnimatorProviderProps {
    /**
     * @deprecated Define sizes using responsive CSS instead. Remove once UIGameCardInventory is converted to ReactJS
     */
    itemSize: number
    isAnimationsEnabled: boolean
    children: React.ReactNode
}

export const UIGameCardAnimatorProvider = ({
    itemSize,
    isAnimationsEnabled,
    children,
}: UIGameCardAnimatorProviderProps) => {
    const [cards, setCards] = useState<UIGameCardAnimatorItemProps[]>([])

    const addCard = useCallback((item: UIGameCardAnimatorItemProps) => {
        if(!isAnimationsEnabled) {
            item.onComplete?.(0)
            return
        }

        setCards((prevCards) => [...prevCards, item])
    }, [isAnimationsEnabled])

    const removeCard = useCallback((id: number) => {
        setCards((prevCards) => prevCards.filter((card) => card.id !== id))
    }, [])

    const forceAnimationComplete = useCallback((id: number) => {
        const card = cards.find((cardItem) => cardItem.id === id)
        if(card) card.onComplete?.(card.id)
    }, [cards])

    useEffect(() => {
        UIGameCardAnimatorEventEmitter.on('addCard', addCard)
        UIGameCardAnimatorEventEmitter.on('removeCard', removeCard)
        UIGameCardAnimatorEventEmitter.on('forceAnimationComplete', forceAnimationComplete)

        return () => {
            UIGameCardAnimatorEventEmitter.off('addCard', addCard)
            UIGameCardAnimatorEventEmitter.off('removeCard', removeCard)
            UIGameCardAnimatorEventEmitter.off('forceAnimationComplete', forceAnimationComplete)
        }
    }, [addCard, removeCard, forceAnimationComplete])

    return (
        <UIGameCardAnimatorContext.Provider value={{
            cards: cards,
            itemSize: itemSize,
        }}>
            {children}
        </UIGameCardAnimatorContext.Provider>
    )
}
